import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";

export const paymentsApi = api
  .enhanceEndpoints({
    addTagTypes: ["fee", "wompiTransaction", "stripeSetupIntent", "customer"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      wompiStatus: builder.query({
        query: ({ cache }) => ({
          url: `payments/wompi/status?${cache}`,
          method: "GET",
        }),
        providesTags: ["wompiTransaction"],
        transformResponse: (response: any) => response.data,
      }),
      wompiFee: builder.mutation({
        query: (body) => ({
          url: "payments/wompi/fee",
          method: "POST",
          body,
        }),
        transformResponse: (response: any) => response.data,
      }),
      wompiSlotBond: builder.mutation({
        query: (body) => ({
          url: "payments/wompi/slot-bond",
          method: "POST",
          body,
        }),
        transformResponse: (response: any) => response.data,
      }),
      wompiAssistedBond: builder.mutation({
        query: (body) => ({
          url: "payments/wompi/assisted-bond",
          method: "POST",
          body,
        }),
        transformResponse: (response: any) => response.data,
      }),
      wompiConfirm: builder.query({
        query: (body) => ({
          url: "api/v1/payment/check",
          method: "POST",
          body,
        }),
        providesTags: ["wompiTransaction"],
        transformResponse: (response: any) => response.data.transaction,
      }),
      wompiPayment: builder.mutation({
        query: (body) => ({
          url: "api/v1/payment",
          method: "POST",
          body,
        }),
        transformResponse: (response: any) => response.data,
        invalidatesTags: ["wompiTransaction"],
      }),
      paymentConfig: builder.query({
        query: ({ id }) => ({
          url: `payments/config`,
          method: RequestMethod.GET,
          params: camelToSnake({
            id,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
      }),
      stripeSetupIntent: builder.query({
        query: ({ userId, paymentMethodType }) => ({
          url: `payments/stripe/setup-intent`,
          method: RequestMethod.GET,
          params: camelToSnake({
            userId,
            paymentMethodType,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["stripeSetupIntent", "customer"],
      }),
      stripeCustomer: builder.query({
        query: () => ({
          url: "payments/stripe/customer",
          method: "GET",
          cache: "no-cache",
        }),
        transformResponse: (response: any) => response?.data,
      }),
      stripeSetupCard: builder.mutation({
        query: ({ paymentMethodId }) => ({
          url: "payments/stripe/setup-card",
          method: "POST",
          body: {
            payment_method_id: paymentMethodId,
          },
          cache: "no-cache",
        }),
        transformResponse: (response: any) => response?.data,
      }),
      stripeSetupSepa: builder.mutation({
        query: (params) => ({
          url: "payments/stripe/setup-sepa",
          method: "POST",
          body: params,
        }),
        transformResponse: (response: any) => response?.data,
      }),
      stripePaymentFee: builder.mutation({
        query: ({ feeId, paymentMethodId, type, ...body }) => {
          return {
            url: "payments/stripe/fee",
            method: "POST",
            body: {
              type,
              fee_id: feeId,
              payment_method_id: paymentMethodId,
              ...body,
            },
          };
        },
        transformResponse: (response: any) => response?.data,
      }),
      stripePaymentBond: builder.mutation({
        query: ({
          paymentMethodId,
          type,
          assistedBondId,
          slotBondId,
          ...body
        }) => ({
          url: "payments/stripe/bond",
          method: "POST",
          body: {
            payment_method_id: paymentMethodId,
            type: type,
            assisted_bond_id: assistedBondId,
            slot_bond_id: slotBondId,
            ...body,
          },
        }),
        transformResponse: (response: any) => response?.data,
      }),
      
      sepaDebitPayment: builder.mutation({
        query: ({
          user_id,
          total,
          ...body
        }) => ({
          url: "payments/customer-to-gym-subscription/setup-intent-dash",
          method: "POST",
          body: {
            user_id: user_id,
            payment_method_type: 'sepa_debit',
            price: total * 100,
            currency: 'eur',
            ...body,
          },
        }),
        transformResponse: (response: any) => response?.data,
      }),

      paymentIntent: builder.mutation({
        query: ({
          user_id,
          payment_method,
          payment_method_type,
          price,
          currency,
          type_product,
          product,

        }) => ({
          url: "payments/customer-to-gym-subscription/payment-intent-dash",
          method: "POST",
          body: {
            user_id,
            payment_method,
            payment_method_type,
            price: price,
            currency,
            type_product,
            product,
          },
        }),
        transformResponse: (response: any) => response?.data,
      }),


    }),
  });

export const {
  usePaymentConfigQuery,
  useStripeSetupIntentQuery,
  useLazyStripeSetupIntentQuery,
  useStripeSetupCardMutation,
  useStripeSetupSepaMutation,
  useStripePaymentFeeMutation,
  useStripePaymentBondMutation,
  useStripeCustomerQuery,
  useWompiPaymentMutation,
  useWompiConfirmQuery,
  useWompiFeeMutation,
  useWompiAssistedBondMutation,
  useWompiSlotBondMutation,
  useWompiStatusQuery,
  useSepaDebitPaymentMutation,
  usePaymentIntentMutation,
} = paymentsApi;
